/**
 * Журнал входов (13.10, владелец: «чтобы в следующий раз было видно, кто заходил»). Только ИО.
 * mode="staff" — последние входы сотрудника (GET /api/staff/:id/access-log);
 * mode="denied" — чужие попытки за 14 дней: Telegram без карточки сотрудника или битая подпись
 * (GET /api/staff/access-denied). Пишет сервер (apps/api/src/utils/accessLog.ts): одна строка
 * на «кто + IP + устройство» раз в 30 минут, хранится 90 дней. Ведётся с 13.10 — раньше пусто.
 */
import { useEffect, useState } from "react";
import { SheetDialog } from "../../components/shared";
import { api } from "../../api/client";
import { fmtOrenAt } from "../../components/modal/blockInfo";
import css from "./accessLog.module.css";

interface Row {
  id: number; at: string; kind: "tma" | "token" | "denied" | "invalid" | "a1c"; staff_id: string | null; telegram_id: number | null;
  ip: string; via: string; country: string; city: string; asn: number | null; as_org: string; ua: string; client: string; path: string;
}

const KIND: Record<Row["kind"], { t: string; cls: string | undefined }> = {
  tma: { t: "вход", cls: css.kOk },
  token: { t: "токен", cls: css.kWarn },
  denied: { t: "чужой TG", cls: css.kBad },
  invalid: { t: "подпись", cls: css.kBad },
  a1c: { t: "загрузчик 1С", cls: css.kWarn },
};

/** Устройство: платформа Telegram-клиента, иначе догадка по User-Agent. */
function device(r: Row): string {
  if (r.client) return r.client;
  const u = r.ua;
  if (/Telegram-Android|Android/i.test(u)) return "Android";
  if (/iPhone|iPad|iOS/i.test(u)) return "iOS";
  if (/Windows/i.test(u)) return "Windows";
  if (/Mac OS X|Macintosh/i.test(u)) return "macOS";
  if (/Linux/i.test(u)) return "Linux";
  return u ? u.slice(0, 40) : "—";
}

function place(r: Row): string {
  const net = r.as_org ? `${r.as_org}${r.asn ? ` · AS${r.asn}` : ""}` : r.asn ? `AS${r.asn}` : "";
  return [r.country, r.city, net].filter(Boolean).join(" · ") || "место неизвестно";
}

export function AccessLogSheet({ open, mode, staffId, name, onClose }: {
  open: boolean; mode: "staff" | "denied"; staffId?: string; name?: string; onClose: () => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setRows(null); setErr("");
    const path = mode === "denied" ? "/api/staff/access-denied" : `/api/staff/${encodeURIComponent(staffId ?? "")}/access-log`;
    api<{ rows: Row[] }>(path)
      .then((r) => { if (alive) setRows(r.rows ?? []); })
      .catch((e: unknown) => { if (alive) setErr(e instanceof Error ? e.message : "Не удалось загрузить журнал"); });
    return () => { alive = false; };
  }, [open, mode, staffId]);

  const title = mode === "denied" ? "Чужие попытки входа · 14 дней" : `Входы · ${name ?? ""}`;
  return (
    <SheetDialog open={open} onDismiss={onClose} ariaLabel={title} maxHeight="80vh">
      <div className={css.head}>
        <span className={css.title}>{title}</span>
        <button type="button" className={css.close} onClick={onClose} aria-label="Закрыть">✕</button>
      </div>
      <div className={css.note}>Ведётся с 13.10: кто, когда, откуда и с какого устройства. Одна строка на устройство раз в 30 минут.</div>
      <div className={css.list}>
        {err ? <div className={css.err}>{err}</div> : null}
        {!err && rows === null ? <div className={css.empty}>Загружаю…</div> : null}
        {rows && rows.length === 0 ? <div className={css.empty}>{mode === "denied" ? "Чужих попыток не было" : "Входов пока нет"}</div> : null}
        {rows?.map((r) => (
          <div key={r.id} className={css.row}>
            <div className={css.line1}>
              <span className={css.at}>{fmtOrenAt(r.at)}</span>
              <span className={`${css.kind} ${KIND[r.kind]?.cls ?? ""}`}>{KIND[r.kind]?.t ?? r.kind}</span>
              <span className={css.dev}>{device(r)}</span>
            </div>
            <div className={css.line2}>
              <span>{place(r)}</span>
              <span className={css.ip} title={r.via === "proxy" ? "через сайт" : r.via === "worker" ? "через чужой сервер — настоящий IP неизвестен" : "напрямую в API"}>
                {r.via === "worker" ? "IP скрыт сервером" : r.ip || "IP неизвестен"}
              </span>
            </div>
            {mode === "denied" || r.kind === "denied" ? (
              <div className={css.line3}>{r.kind === "invalid" ? "неверная подпись или токен" : `Telegram ID ${r.telegram_id ?? "—"}`}</div>
            ) : null}
          </div>
        ))}
      </div>
    </SheetDialog>
  );
}
